/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Inject, Input, OnDestroy, OnInit, signal} from '@angular/core';
import * as _ from 'lodash';
import {Subscription} from 'rxjs';
import {UIRouterModule} from '@uirouter/angular';

import template from './advertiser-earnings-widget.component.html';
import {StatsApiService} from '../../common/controller/stats-api.service';
import {ChartOptionsUtil} from '../../common/options/chart-options-util.service';
import {deferSignalStateWrite} from '../../common/util/template-state';
import {APP_SHARED} from '../../common/app-shared';

@Component({
  standalone: true,
  imports: [...APP_SHARED, UIRouterModule],
  selector: 'adn-advertiser-earnings-widget',
  template
})
export class AdvertiserEarningsWidgetComponent implements OnInit, OnDestroy {
  @Input() widgetType: any;

  readonly obj = signal<any>(null);
  readonly tableFields = signal<any[]>([]);
  readonly fieldComparisons = signal<any[]>([]);
  readonly childParam = 'earningsAccount';
  readonly rows = signal<any[]>([]);
  readonly totals = signal<any>(null);
  readonly isDone = signal(false);
  readonly hasError = signal(false);
  private statsSubscription?: Subscription;

  constructor(
    private readonly statsApi: StatsApiService,
    @Inject(ChartOptionsUtil) private readonly ChartOptionsUtil: any
  ) {
  }

  ngOnInit(): void {
    if (!_.get(this.widgetType, ['obj', 'id'])) {
      return;
    }

    const chartOptions = this.ChartOptionsUtil.getOptions('ADVERTISER');
    this.tableFields.set(_.filter(chartOptions.totalsFields, (field: any) => {
      return this.widgetType.fields.indexOf(field.id) > -1;
    }));
    this.obj.set(this.widgetType.obj);
    this.fieldComparisons.set(this.widgetType.fieldComparisons || []);

    const params: any = {
      aggregateTo: 'ADVERTISER',
      groupBy: 'EARNINGS_ACCOUNT',
      includeAll: false,
      advertiserId: this.obj().id,
      includeChunkRange: true,
      startDate: this.widgetType.startDate,
      endDate: this.widgetType.endDate
    };
    if (_.isArray(this.widgetType.objMulti) && this.widgetType.objMulti.length > 0) {
      params.earningsAccountId = _.join(_.map(this.widgetType.objMulti, (om: any) => om.id), ';');
    }

    this.isDone.set(false);
    this.hasError.set(false);
    this.statsSubscription = this.statsApi.get(params).subscribe({next: (data: any) => {
      deferSignalStateWrite(() => {
        this.rows.set(data.chunks || []);
        this.totals.set(data.totals);
        this.isDone.set(true);
      });
    }, error: () => {
      deferSignalStateWrite(() => {
        this.hasError.set(true);
        this.isDone.set(true);
      });
    }});
  }

  ngOnDestroy(): void {
    this.statsSubscription?.unsubscribe();
  }

  childData(row: any): any {
    return row?.[this.childParam];
  }

  showFieldLabel(field: any): boolean {
    return !field.abbr || field.extra;
  }

  comparisonRatio(value: any, target: number): number {
    const amount = value?.amount ?? value;
    return amount / target;
  }

  get showNoDataWarning(): boolean {
    const rows = this.rows();
    return !(rows.length && rows[0]?.chunks);
  }

  get showTable(): boolean {
    const rows = this.rows();
    return !!(rows.length && rows[0]?.chunks);
  }
}
